/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { FC, useState, useEffect, useMemo, useCallback } from "react"
import { useHistory, useParams } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Table } from "@duxton-web/ui"
import { includes, replace, isEmpty, debounce } from "lodash"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { ItemName } from "components/ItemName"
import {
  ItemAvailabilityDropdownDuxton,
  FilterDropdownDuxton,
} from "components/dropdowns"
import { Item, SellingTime, SellingTimeMetaData } from "types/models/menu"
import { actionCreator } from "store/store"
import {
  AvailableStatusesText,
  availableStatuses,
  availableStatusesUsage,
} from "utils/item_helper"
import { AppState } from "store/store.type"
// import from root will cause `node-sass` not found.
import { MarketingBannerV2 } from "troy-common/marketing-banner"
import { marketingBannerStyle } from "pages/menu/style"
import { TroyWebTracker } from "utils/tracking"
import { MENU_DETAIL } from "constants/routes"
import {
  EventAction,
  EventCategory,
  SearchItemTrigger,
} from "constants/tracking"
import { storageHelper } from "utils/storage"
import { ITEM_SEARCH_LIMIT } from "components/search/searchBarConstants"
import { DEFAULT_DEBOUNCE_WAIT_TIME } from "constants/ui"
import { getCategoryList, enrichSearchResultItems } from "utils/menu_helper"
import { OverflowTip, InfiniteTable } from "components/common"
import { fullPathWithSearchParams } from "common/utils/uri"
import useInventoryContext from "hooks/useInventoryContext"
import {
  getIsAlwaysAvailable,
  getSellingTimeByID,
} from "utils/selling_time_helper"
import { ItemStockManagement } from "components/ItemStockManagement"
import useMenuContext from "hooks/useMenuContext"
import { useSellingTimes } from "hooks/useSellingTimes"
import { menuEditorTabKeys, ColumnKey } from "constants/menu"
import { parseJSONSafely } from "utils/misc"
import { useMenuItemSearch } from "hooks/useMenuItemSearch"
import { getGrabXValueFromLocalStorage, MerchantPortalFeatureFlag } from "grabx"
import { EmptyItems, EmptySectionCategoryWrapper } from "../empty"
import { ItemListHeader } from "./ItemListHeader"
import { useStockManagementAutoScrollContext } from "../StockManagementAutoScrollContext"
import { useItemListItems } from "./hooks/useItemListItems"
import { useItemListDepartments } from "./hooks/useItemListDepartments"
import { useItemListSubDepartments } from "./hooks/useItemListSubDepartments"
import styles from "./ItemList.modules.css"

const ALL = "ALL"
const ALL_ARRAY = [ALL]

interface ItemListProps {
  routePrefix?: string
}

export const ItemList: FC<ItemListProps> = ({ routePrefix }: ItemListProps) => {
  const dispatch = useDispatch()
  const [lastPriceOrder, setLastPriceOrder] = useState<string>("ascend")
  const [lastFilterCategory, setLastFilterCategory] = useState<string[]>([ALL])
  const [lastFilterDepartment, setLastFilterDepartment] = useState<string[]>([
    ALL,
  ])
  const [lastFilterSubDepartment, setLastFilterSubDepartment] = useState<
    string[]
  >([ALL])
  const [lastFilterStatus, setLastFilterStatus] = useState<string[]>([ALL])

  const isHideItemEnabled = getGrabXValueFromLocalStorage<boolean>(
    MerchantPortalFeatureFlag.portalEnableHideItem,
  )

  const { itemID, tabKey } = useParams<{
    itemID: string
    tabKey: string
  }>()

  const [
    categoryFilterDropdownVisible,
    setCategoryFilterDropdownVisible,
  ] = useState(false)

  const [
    sectionFilterDropdownVisible,
    setSectionFilterDropdownVisible,
  ] = useState(false)

  const [
    departmentFilterDropdownVisible,
    setDepartmentFilterDropdownVisible,
  ] = useState(false)

  const [
    subDepartmentFilterDropdownVisible,
    setSubDepartmentFilterDropdownVisible,
  ] = useState(false)

  const [
    statusFilterDropdownVisible,
    setStatusFilterDropdownVisible,
  ] = useState(false)

  const [
    sellingTimeFilterDropdownVisible,
    setSellingTimeFilterDropdownVisible,
  ] = useState(false)

  const [isFilterBySellingTime, setIsFilterBySellingTime] = useState(false)

  const [filterBySellingTimeCount, setFilterBySellingTimeCount] = useState(0)

  const [searchFilter, setSearchFilter] = useState("")

  const [sortedInfo, setSortedInfo] = useState<any>({})

  const [filter, setFilter] = useState<{
    [key: string]: string[] | undefined
  }>({})

  const { sellingTimes } = useSellingTimes()

  // Use hooks to get data (from query when enablePartialMenu, otherwise from Redux)
  const { items, isLoading: isItemsLoading } = useItemListItems()
  const { departments: departmentsForFilters } = useItemListDepartments()
  const { subDepartments } = useItemListSubDepartments()

  const {
    outlet,
    outletV2,
    merchantID,
    menu: { sections },
    categories,
    isMartDepartmentStructure,
    isMartStore,
    isSellingTimeStructure,
    menuGroupDetail: { menuGroupID },
    merchantGroupID,
    enableTroyNewMerchantEndpoint,
    searchResults,
    searchItemLoading,
    overview: { search: searchValue },
  } = useSelector((state: AppState) => state.menu)

  const { menu } = useSelector((state: AppState) => state.menu)
  const { disableAvailability, hideCreateAction } = useInventoryContext()
  const { isServiceBasedMenuEnabled } = useMenuContext()
  const {
    setShouldAutoScrollToStockManagement,
  } = useStockManagementAutoScrollContext()
  const { isSearchEnabled, hasActiveSearch } = useMenuItemSearch()

  const enableStockManagement = enableTroyNewMerchantEndpoint
    ? !!outletV2?.merchantFlag?.isInventoryManagementEnabled
    : !!outlet?.isInventoryManagementEnabled

  const history = useHistory()

  const handleSearchChange = useCallback(
    debounce((value: string) => {
      dispatch(actionCreator.menu.setSearchText(value))
      // Validate search length limit - don't call API if exceeds
      if (value.length > ITEM_SEARCH_LIMIT) {
        return
      }

      // For API-based search (when feature flag is enabled)
      if (isSearchEnabled && value.trim().length > 0) {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuItemsTab,
          action: EventAction.searchItem,
          labels: {
            ...(merchantID
              ? { STORE_ID: merchantID }
              : { MERCHANT_GROUP_ID: merchantGroupID }),
            SEARCH_TEXT: value.trim(),
            TRIGGER: SearchItemTrigger.searchInput,
          },
        })

        dispatch(
          actionCreator.menu.searchItems({
            merchantID,
            merchantGroupID,
            searchKey: value.trim(),
          }),
        )
      }

      // Always update local search filter for fallback/non-API search
      setSearchFilter(value)
    }, DEFAULT_DEBOUNCE_WAIT_TIME),
    [dispatch, isSearchEnabled, merchantID, merchantGroupID],
  )

  const handleSearchClear = () => {
    dispatch(actionCreator.menu.setSearchText(""))
    setSearchFilter("")

    // Clear search results if feature flag is enabled
    if (isSearchEnabled) {
      dispatch(actionCreator.menu.clearSearchResults())
    }
  }

  // Determine data source: use searchResults when search is active, otherwise use items
  const dataSource =
    hasActiveSearch && searchResults
      ? enrichSearchResultItems(searchResults)
      : items

  const matchesNameFilter = (item: Item) => {
    // Skip name filter when using API search (already filtered by API)
    if (hasActiveSearch) return true
    // Apply local name filter for non-API search
    return includes(item.itemName?.toLowerCase(), searchFilter?.toLowerCase())
  }

  const filteredItems = dataSource.filter(
    item =>
      matchesNameFilter(item) &&
      (!filter.category ||
        filter.category.includes(ALL) ||
        filter.category.includes(item.categoryID || "")) &&
      (!filter.sectionName ||
        filter.sectionName.includes(ALL) ||
        filter.sectionName.includes(item.sectionID || "")) &&
      (!filter.sellingTime ||
        filter.sellingTime.includes(ALL) ||
        filter.sellingTime.includes(item.sellingTimeID || "")) &&
      (!filter.department ||
        filter.department.includes(ALL) ||
        filter.department.includes(item.departmentID || "")) &&
      (!filter.subDepartment ||
        filter.subDepartment.includes(ALL) ||
        filter.subDepartment.includes(item.itemClassID || "")) &&
      (!filter.status ||
        filter.status.includes(ALL) ||
        filter.status.includes(String(item.availableStatus))),
  )

  const labels: any = merchantID
    ? { STORE_ID: merchantID }
    : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }

  const availableStatusesText = AvailableStatusesText(
    availableStatusesUsage.TABLE_FILTER,
  )
  // handle filter dropdown visible change
  useEffect(() => {
    if (categoryFilterDropdownVisible) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuItemsTab,
        action: EventAction.clickFilterCategory,
        labels,
      })
    }
    if (departmentFilterDropdownVisible) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuItemsTab,
        action: EventAction.clickFilterDepartment,
        labels,
      })
    }
    if (subDepartmentFilterDropdownVisible) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuItemsTab,
        action: EventAction.clickFilterSubDepartment,
        labels,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categoryFilterDropdownVisible,
    departmentFilterDropdownVisible,
    subDepartmentFilterDropdownVisible,
  ])

  const showSectionColumn = useMemo(() => {
    const sectionMap: {
      [key: string]: number
    } = {}
    const output = items.some(item => {
      if (item.sectionID) {
        if (sectionMap[item.sectionID] === undefined) {
          sectionMap[item.sectionID] = 0
        }
        sectionMap[item.sectionID] += 1
        if (Object.keys(sectionMap).length <= 1) {
          return false
        }
        // Object.keys(sectionMap).length > 1
        return true
      }
      return false
    })
    return output
  }, [items])

  useEffect(() => {
    const f = filter
    // handle category filter changed
    if (f.category) {
      if (JSON.stringify(f.category) !== JSON.stringify(lastFilterCategory)) {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuItemsTab,
          action: EventAction.selectFilterCategory,
          labels: {
            ...labels,
            CATEGORY_ID: f.category,
          },
        })
        setLastFilterCategory(f.category)
      }
    }
    // handle department filter changed
    if (f.department) {
      if (
        JSON.stringify(f.department) !== JSON.stringify(lastFilterDepartment)
      ) {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuItemsTab,
          action: EventAction.selectFilterDepartment,
          labels: {
            ...labels,
            CATEGORY_ID: f.department,
          },
        })
        setLastFilterDepartment(f.department)
      }
    }
    // handle sub department filter changed
    if (f.subDepartment) {
      if (
        JSON.stringify(f.subDepartment) !==
        JSON.stringify(lastFilterSubDepartment)
      ) {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuItemsTab,
          action: EventAction.selectFilterSubDepartment,
          labels: {
            ...labels,
            SUB_CATEGORY_ID: f.subDepartment,
          },
        })
        setLastFilterSubDepartment(f.subDepartment)
      }
    }
    // handle status filter changed
    if (f.status) {
      if (JSON.stringify(f.status) !== JSON.stringify(lastFilterStatus)) {
        TroyWebTracker.sendEvent({
          category: EventCategory.menuItemsTab,
          action: EventAction.selectFilterStatus,
          labels: {
            ...labels,
            VALUE: f.status,
          },
        })
        setLastFilterStatus(f.status)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter])

  function handlePriceOrderChange(sorter: any) {
    const sortOrder = sorter?.order ?? ""
    if (lastPriceOrder !== sortOrder) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuItemsTab,
        action: EventAction.clickSortPrice,
        labels: {
          ...labels,
          SORT_ORDER: sortOrder,
        },
      })
      setLastPriceOrder(sortOrder)
    }
  }

  const handleFilterStatusVisibleChange = (visible: boolean) => {
    if (visible) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuItemsTab,
        action: EventAction.clickFilterStatus,
        labels,
      })
    }
    setStatusFilterDropdownVisible(visible)
  }

  const toggleDrawer = (item: Item) => {
    if (!isEmpty(item)) {
      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewTab,
        action: EventAction.clickEditItem,
        labels: {
          ...(merchantID
            ? { STORE_ID: merchantID }
            : {
                MERCHANT_GROUP_ID: merchantGroupID,
                MENU_GROUP_ID: menuGroupID,
              }),
          ITEM_ID: item.itemID,
        },
      })

      dispatch(
        actionCreator.menu.toggleDrawer({
          item,
          showItem: true,
          sourceShowItem:
            tabKey === menuEditorTabKeys.menuOverview
              ? EventCategory.menuOverviewModifyItem
              : EventCategory.menuItemsModifyitem,
        }),
      )
    }
  }

  const handleSelectItem = (item: Item) => {
    const nextPath = `${MENU_DETAIL({
      isMartStore,
      merchantID: menuGroupID || merchantID,
      tabKey: "items",
      itemID: item.itemID,
      customRoutePrefix: routePrefix,
    })}`
    const destination = fullPathWithSearchParams({
      nextPath,
    })
    history.replace(destination)

    toggleDrawer(item)
  }

  // deeplink when there is itemParam on first page load
  useEffect(() => {
    if (itemID) {
      const selectedItem = items.find(item => item.itemID === itemID)
      if (selectedItem) {
        toggleDrawer(selectedItem)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reset filters and sort order when search starts loading
  useEffect(() => {
    if (searchItemLoading) {
      setFilter({})
      setSortedInfo({})
    }
  }, [searchItemLoading])

  const isLoading = isItemsLoading || searchItemLoading
  const hasItems = isLoading || items.length > 0

  const textMap = useTranslationsMapTyped({
    emptyText: "menu.item.modifierGroup.noDataAvailable",
    status: "menu.items.headings.status",
    allItems: "menu.items.filters.allItems",
    allCategories: "menu.items.filters.allCategories",
    allSubCategories: "menu.items.filters.allSubCategories",
    allSections: "menu.items.filters.allSections",
    alwaysAvailable: "menu.sellingTime.dropdown.alwaysAvailable",
    itemName: "menu.items.headings.itemName",
    price: "menu.items.headings.price",
    section: "menu.items.headings.section",
    category: "menu.items.headings.category",
    subCategory: "catalogue.items.headings.subCategory",
    itemCount: "menu.items.messages.count",
    filterConfirm: "menu.stores.filter.ok",
    stock: "inventory.stock.table.column",
    characterExceed: "common.characterExceed",
  })

  const statusFilterItems = useMemo(() => {
    const statusKeys = Object.keys(availableStatusesText).filter(
      value =>
        isHideItemEnabled ||
        Number(value) !== availableStatuses.HIDDEN_UNTIL_REACTIVATED,
    )

    return [
      {
        value: ALL,
        text: textMap.allItems,
      },
      ...statusKeys.map((value: string) => ({
        value,
        text: availableStatusesText[Number(value)],
      })),
    ]
  }, [availableStatusesText, isHideItemEnabled, textMap.allItems])

  const searchError =
    (searchValue || "").length > ITEM_SEARCH_LIMIT
      ? textMap.characterExceed
          .replace("<current>", `${(searchValue || "").length}`)
          .replace("<limit>", `${ITEM_SEARCH_LIMIT}`)
      : ""

  const getCategoryFilters = () => {
    const categoryFilters = []
    const menuCategories = getCategoryList(menu)
    const allItemsCount = menuCategories.reduce(
      (sum, category) => sum + (category.items?.length || 0),
      0,
    )
    categoryFilters.push({
      value: ALL,
      text: textMap.allCategories,
      subtext: replace(textMap.itemCount, "$items", `${allItemsCount}`),
    })
    const pushedSections = new Set<string>()
    menuCategories.forEach(category => {
      const { sectionID, sectionName } = category
      if (sectionID && sectionName && !pushedSections.has(sectionID)) {
        categoryFilters.push({
          value: sectionID,
          text: sectionName.toUpperCase(),
          className: styles.customFilterDropdownGroupItem,
          unselectable: true,
        })
        pushedSections.add(sectionID)
      }
      categoryFilters.push({
        value: category.categoryID,
        text: category.categoryName,
        subtext: replace(
          textMap.itemCount,
          "$items",
          `${category.items?.length || 0}`,
        ),
      })
    })
    return categoryFilters
  }

  const sectionFilters = useMemo(() => {
    const sectionFiltersList = []
    sectionFiltersList.push({
      value: ALL,
      text: textMap.allSections,
    })
    sections?.forEach(section => {
      sectionFiltersList.push({
        value: section.sectionID,
        text: section.sectionName,
      })
    })
    return sectionFiltersList
  }, [sections, textMap.allSections])

  const getDepartmentFilters = () => {
    const departmentFilters = []
    // Use departments from hook - it already handles both query and Redux cases
    const allItemsCount = departmentsForFilters.reduce(
      (sum, department) => sum + (department.totalItems ?? 0),
      0,
    )
    departmentFilters.push({
      value: ALL,
      text: textMap.allCategories,
      subtext: replace(textMap.itemCount, "$items", `${allItemsCount}`),
    })

    const pushedSections = new Set<string>()
    departmentsForFilters.forEach(department => {
      const { sectionID, sectionName } = department
      if (sectionID && sectionName && !pushedSections.has(sectionID)) {
        departmentFilters.push({
          value: sectionID,
          text: sectionName.toUpperCase(),
          className: styles.customFilterDropdownGroupItem,
          unselectable: true,
        })
        pushedSections.add(sectionID)
      }
      departmentFilters.push({
        value: department.itemClassID,
        text: department.itemClassName,
        subtext: replace(
          textMap.itemCount,
          "$items",
          `${department.totalItems}`,
        ),
      })
    })
    return departmentFilters
  }

  const getSubDepartmentFilters = () => {
    const subDepartmentFilters = []
    const allItemsCount = subDepartments.reduce(
      (sum, subDepartment) => sum + (subDepartment.totalItems ?? 0),
      0,
    )
    subDepartmentFilters.push({
      value: ALL,
      text: textMap.allSubCategories,
      subtext: replace(textMap.itemCount, "$items", `${allItemsCount}`),
    })

    // Use departments from hook - it already handles both query and Redux cases
    departmentsForFilters.forEach(department => {
      if (department?.subDepartments?.length) {
        subDepartmentFilters.push({
          value: department.itemClassID,
          text: department.itemClassName.toUpperCase(),
          className: styles.customFilterDropdownGroupItem,
          unselectable: true,
        })
        department.subDepartments.forEach(subDepartment => {
          subDepartmentFilters.push({
            value: subDepartment.itemClassID,
            text: subDepartment.itemClassName,
            subtext: replace(
              textMap.itemCount,
              "$items",
              `${subDepartment.totalItems}`,
            ),
          })
        })
      }
    })
    return subDepartmentFilters
  }

  const getColMaxWidth = () => {
    if (isMartDepartmentStructure && isSellingTimeStructure) return "11vw"
    if (isMartDepartmentStructure || isSellingTimeStructure) return "18vw"
    return "25vw"
  }

  const getSellingTimeName = (sellingTime?: SellingTime) => {
    if (sellingTime) {
      const isAlwaysAvailable = getIsAlwaysAvailable(sellingTime)
      if (isAlwaysAvailable) return textMap.alwaysAvailable
      return sellingTime.name
    }
    return ""
  }

  const getSellingTimeDataForTracker = (): {
    selectedSellingTimes: SellingTimeMetaData[]
    selectedSellingTimeNames: string[]
    sellingTimeIDs: string[]
  } => {
    const result: {
      selectedSellingTimes: SellingTimeMetaData[]
      selectedSellingTimeNames: string[]
      sellingTimeIDs: string[]
    } = {
      selectedSellingTimes: [],
      selectedSellingTimeNames: [],
      sellingTimeIDs: [],
    }

    filter?.sellingTime?.forEach(value => {
      const searchedSellingTime = sellingTimes.find(
        sellingTime => sellingTime.sellingTimeID === value,
      )

      if (searchedSellingTime) {
        result.selectedSellingTimes.push(searchedSellingTime)
        result.selectedSellingTimeNames.push(searchedSellingTime.name)
        result.sellingTimeIDs.push(searchedSellingTime.sellingTimeID)
      }
    })

    return result
  }

  useEffect(() => {
    // isFilterBySellingTime prevent double sentEvent when filteredItems state changes
    // filterBySellingTimeCount to prevent sendEvent when first time rendering ItemList component
    if (!isFilterBySellingTime || filterBySellingTimeCount === 1) {
      return
    }

    TroyWebTracker.sendEvent({
      category: EventCategory.menuGroupItemsTab,
      action: EventAction.selectSellingTime,
      labels: {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        NUM_ITEM: filteredItems.length,
        SELLING_TIME_COUNT: sellingTimes?.length,
      },
    })

    setIsFilterBySellingTime(false)
  }, [filteredItems, filterBySellingTimeCount])

  const filterSellingTimeDropdown = (filteredValue: string[]) => {
    setFilter({
      ...filter,
      sellingTime: filteredValue,
    })

    setFilterBySellingTimeCount(filterBySellingTimeCount + 1)
    setIsFilterBySellingTime(true)
  }

  return (
    <div className={styles.container}>
      <div className={styles.headerWrapper}>
        <ItemListHeader
          onSetFilter={handleSearchChange}
          onClear={handleSearchClear}
          filteredItems={filteredItems}
          hasCreateAction={!hideCreateAction}
          searchError={searchError}
          hideDropdownResults={isSearchEnabled}
        />
      </div>
      <div className={styles.contentWrapper}>
        <EmptySectionCategoryWrapper
          categories={categories}
          hideCreate={hideCreateAction}
        >
          <InfiniteTable
            rowKey="itemID"
            data-testid="item-list-table"
            dataSource={filteredItems}
            loading={isLoading}
            onChange={(_: any, f: any, s: any) => {
              setFilter({
                ...filter,
                ...f,
              })
              setSortedInfo(s || {})
              handlePriceOrderChange(s)
            }}
            onRow={(record: any) => ({
              onClick: () => {
                handleSelectItem(record)
              },
            })}
            locale={{
              emptyText: hasItems ? (
                textMap.emptyText
              ) : (
                <EmptyItems hideCreate={hideCreateAction} />
              ),
              filterConfirm: textMap.filterConfirm,
            }}
            className={styles.tableWrapper}
          >
            <Table.Column
              key="itemName"
              data-testid="item-name-column"
              title={textMap.itemName}
              render={(_: any, item: Item) => {
                return (
                  <div
                    className={styles.itemNameWrapper}
                    style={{ maxWidth: getColMaxWidth() }}
                  >
                    <ItemName item={item} />
                  </div>
                )
              }}
            />
            <Table.Column
              key="price"
              data-testid="price-column"
              title={replace(
                textMap.price,
                "<currency.symbol>",
                storageHelper.getUserCurrency(),
              )}
              dataIndex={
                isServiceBasedMenuEnabled ? "priceRange" : "priceDisplay"
              }
              sorter={(a: Item, b: Item) =>
                Number(a.priceInMin ?? 0) - Number(b.priceInMin ?? 0)
              }
              sortOrder={
                sortedInfo.columnKey === ColumnKey.price
                  ? sortedInfo.order
                  : null
              }
            />
            {showSectionColumn && (
              <Table.Column
                key="sectionName"
                title={textMap.section}
                dataIndex="sectionName"
                filtered={
                  !!filter.sectionName && !filter.sectionName.includes(ALL)
                }
                onFilterDropdownOpenChange={(visible: boolean) =>
                  setSectionFilterDropdownVisible(visible)
                }
                filterDropdownOpen={sectionFilterDropdownVisible}
                filterDropdown={
                  <FilterDropdownDuxton
                    key={`sectionFilter-${Date.now()}`}
                    title="section-name"
                    items={sectionFilters}
                    filteredValue={filter.sectionName ?? ALL_ARRAY}
                    setVisible={setSectionFilterDropdownVisible}
                    onChange={filteredValue =>
                      setFilter({
                        ...filter,
                        sectionName: filteredValue,
                      })
                    }
                  />
                }
              />
            )}
            {!isMartDepartmentStructure && (
              <Table.Column
                key="category"
                data-testid="category-column"
                title={textMap.category}
                dataIndex="categoryName"
                filtered={!!filter.category && !filter.category.includes(ALL)}
                onFilterDropdownOpenChange={(visible: boolean) =>
                  setCategoryFilterDropdownVisible(visible)
                }
                filterDropdownOpen={categoryFilterDropdownVisible}
                filterDropdown={
                  <FilterDropdownDuxton
                    key={`categoryFilter-${Date.now()}`}
                    title="category"
                    items={getCategoryFilters()}
                    filteredValue={filter.category ?? ALL_ARRAY}
                    setVisible={setCategoryFilterDropdownVisible}
                    onChange={filteredValue =>
                      setFilter({
                        ...filter,
                        category: filteredValue,
                      })
                    }
                    filterConfirmText={textMap.filterConfirm}
                  />
                }
                render={(_: any, item: Item) => {
                  return (
                    <OverflowTip style={{ maxWidth: getColMaxWidth() }}>
                      {item.categoryName}
                    </OverflowTip>
                  )
                }}
              />
            )}
            {isMartDepartmentStructure && (
              <Table.Column
                key="department"
                data-testid="department-column"
                title={textMap.category}
                dataIndex="departmentName"
                filtered={
                  !!filter.department && !filter.department.includes(ALL)
                }
                onFilterDropdownOpenChange={(visible: boolean) =>
                  setDepartmentFilterDropdownVisible(visible)
                }
                filterDropdownOpen={departmentFilterDropdownVisible}
                filterDropdown={
                  <FilterDropdownDuxton
                    key={`departmentFilter-${Date.now()}`}
                    items={getDepartmentFilters()}
                    filteredValue={filter.department ?? ALL_ARRAY}
                    setVisible={setDepartmentFilterDropdownVisible}
                    onChange={filteredValue =>
                      setFilter({
                        ...filter,
                        department: filteredValue,
                      })
                    }
                    filterConfirmText={textMap.filterConfirm}
                  />
                }
                render={(_: any, item: Item) => {
                  return (
                    <OverflowTip style={{ maxWidth: getColMaxWidth() }}>
                      {item.departmentName}
                    </OverflowTip>
                  )
                }}
              />
            )}
            {isMartDepartmentStructure && (
              <Table.Column
                key="subDepartment"
                data-testid="sub-department-column"
                title={textMap.subCategory}
                dataIndex="subDepartmentName"
                filtered={
                  !!filter.subDepartment && !filter.subDepartment.includes(ALL)
                }
                onFilterDropdownOpenChange={(visible: boolean) =>
                  setSubDepartmentFilterDropdownVisible(visible)
                }
                filterDropdownOpen={subDepartmentFilterDropdownVisible}
                filterDropdown={
                  <FilterDropdownDuxton
                    key={`subDepartmentFilter-${Date.now()}`}
                    items={getSubDepartmentFilters()}
                    filteredValue={filter.subDepartment ?? ALL_ARRAY}
                    setVisible={setSubDepartmentFilterDropdownVisible}
                    onChange={filteredValue =>
                      setFilter({
                        ...filter,
                        subDepartment: filteredValue,
                      })
                    }
                    filterConfirmText={textMap.filterConfirm}
                  />
                }
                render={(_: any, item: Item) => {
                  return (
                    <OverflowTip style={{ maxWidth: getColMaxWidth() }}>
                      {item.subDepartmentName}
                    </OverflowTip>
                  )
                }}
              />
            )}
            {isSellingTimeStructure && (
              <Table.Column
                key="sellingTime"
                data-testid="selling-time-column"
                title="Selling Time"
                dataIndex="sellingTime"
                filtered={
                  !!filter.sellingTime && !filter.sellingTime.includes(ALL)
                }
                onFilterDropdownOpenChange={(visible: boolean) => {
                  setSellingTimeFilterDropdownVisible(visible)

                  if (visible) {
                    const sellingTimeDataForTracker = getSellingTimeDataForTracker()

                    TroyWebTracker.sendEvent({
                      category: EventCategory.menuGroupItemsTab,
                      action: EventAction.clickSellingTime,
                      labels: {
                        ...(merchantID
                          ? { STORE_ID: merchantID }
                          : {
                              MERCHANT_GROUP_ID: merchantGroupID,
                              MENU_GROUP_ID: menuGroupID,
                            }),
                        IS_DEFAULT:
                          sellingTimeDataForTracker.selectedSellingTimes
                            .length === 0,
                        SELLING_TIME_COUNT: sellingTimes?.length,
                      },
                    })
                  }
                }}
                filterDropdownOpen={sellingTimeFilterDropdownVisible}
                filterDropdown={
                  <FilterDropdownDuxton
                    key={`sellingTimeFilter-${Date.now()}`}
                    title="selling-time"
                    items={[
                      {
                        value: ALL,
                        text: textMap.allItems,
                      },
                      ...sellingTimes.map(sellingTime => ({
                        value: sellingTime.sellingTimeID,
                        text: getSellingTimeName(sellingTime),
                      })),
                    ]}
                    filteredValue={filter.sellingTime ?? ALL_ARRAY}
                    setVisible={setSellingTimeFilterDropdownVisible}
                    onChange={filterSellingTimeDropdown}
                    filterConfirmText={textMap.filterConfirm}
                  />
                }
                render={(_: any, item: Item) => {
                  return (
                    <OverflowTip style={{ maxWidth: getColMaxWidth() }}>
                      {getSellingTimeName(
                        getSellingTimeByID(item.sellingTimeID, sellingTimes),
                      )}
                    </OverflowTip>
                  )
                }}
              />
            )}
            {enableStockManagement && (
              <Table.Column
                key="stockManagement"
                data-testid="stock-management-column"
                title={textMap.stock}
                render={(_: any, item: Item) => {
                  return (
                    <ItemStockManagement
                      item={item}
                      maxWidth={getColMaxWidth()}
                      onClick={() => {
                        handleSelectItem(item)
                        setShouldAutoScrollToStockManagement(true)
                      }}
                    />
                  )
                }}
                sorter={(a: Item, b: Item) =>
                  Number(a.itemStock?.currentStock ?? Infinity) -
                  Number(b.itemStock?.currentStock ?? Infinity)
                }
                sortOrder={
                  sortedInfo.columnKey === ColumnKey.stockManagement
                    ? sortedInfo.order
                    : null
                }
              />
            )}
            {!disableAvailability && (
              <Table.Column
                key="status"
                data-testid="status-column"
                title={textMap.status}
                dataIndex="availableStatus"
                filtered={!!filter.status && !filter.status.includes(ALL)}
                width={200}
                render={(availableStatus: number, item: Item) => (
                  <div className={styles.statusWrapper}>
                    <ItemAvailabilityDropdownDuxton item={item} />
                  </div>
                )}
                onFilterDropdownOpenChange={(visible: boolean) =>
                  handleFilterStatusVisibleChange(visible)
                }
                filterDropdownOpen={statusFilterDropdownVisible}
                filterDropdown={
                  <FilterDropdownDuxton
                    key={`statusFilter-${Date.now()}`}
                    title="status"
                    items={statusFilterItems}
                    filteredValue={filter.status ?? ALL_ARRAY}
                    setVisible={setStatusFilterDropdownVisible}
                    onChange={(filteredValue: any) =>
                      setFilter({
                        ...filter,
                        status: filteredValue,
                      })
                    }
                    filterConfirmText={textMap.filterConfirm}
                  />
                }
              />
            )}
          </InfiniteTable>

          <MarketingBannerV2
            css={marketingBannerStyle}
            variant="inventory"
            tab="Inventory"
            subtab="Item"
            trackEvent={(
              category: string,
              action: string,
              labelsString: string,
            ) => {
              TroyWebTracker.sendEvent({
                // @ts-ignore
                category,
                // @ts-ignore
                action,
                labels: {
                  ...parseJSONSafely(labelsString ?? "{}"),
                },
              })
            }}
          />
        </EmptySectionCategoryWrapper>
      </div>
    </div>
  )
}
